<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Kundenübersicht
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Gruppenmaßnahmen</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :unemployedDropdown="unemployedDropdown"
            :unemployedStatusDropdown="unemployedStatusFiltered"
            :operationTypeDropdown="groupCoachingOperationTypes"
            @search="getCoachingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage
                :values="[10, 25, 50, 100]"
                @update:perPage="getCoachingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto pt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Maßnahmentyp</th>
              <th class="text-nowrap">Vollständiger Name</th>
              <th>Status</th>
              <th>JC/AA Nr.</th>
              <th>Standort</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Abschlusseinheit</th>
              <th>Stand</th>
              <th class="text-nowrap">Stand %</th>
              <th>Anwesend</th>
              <th>Krank</th>
              <th>Entschuldigt</th>
              <th>Unentschuldigt</th>
              <th>Unterschrift fehlt</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in coachingList.data" :key="coaching.id">
              <td>{{ coaching?.nameOfCoaching.operationTypeTranslated }}</td>
              <td>{{ coaching?.unemployed?.firstName }} {{ coaching?.unemployed?.lastName }}</td>
              <td>{{ coaching?.statusTranslated }}</td>
              <td>{{ coaching?.unemployed?.aaNumber }}</td>
              <td>{{ coaching?.unemployed?.location?.name }}</td>
              <td>{{ coaching.startDate }}</td>
              <td>{{ coaching.endDate }}</td>
              <td>{{ coaching?.finishedAt ?? '-' }}</td>
              <td>{{ coaching.unitsDone }}</td>
              <td>{{ coaching.unitsDonePercent ? coaching.unitsDonePercent + ' %' : '-' }}</td>
              <td class="text-end">{{ coaching?.countPresentStatusUnits }}</td>
              <td class="text-end">{{ coaching?.countIllStatusUnits }}</td>
              <td class="text-end">{{ coaching?.countExcusedStatusUnits }}</td>
              <td class="text-end">{{ coaching?.countNotExcusedStatusUnits }}</td>
              <td class="text-end">{{ coaching?.notSignedUnits }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="coachingList?.pagination?.total_pages > 1"
            :totalPages="coachingList?.pagination?.total_pages"
            :currentPage="coachingList?.pagination?.current_page"
            :totalItems="coachingList?.pagination?.total_items"
            :perPage="coachingList?.pagination?.items_per_page"
            @pagechanged="getCoachingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsUnemployedOverviewService from '@/api/Analytics/Unemployed/Overview';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import {getAllUnemployedDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {computed, onMounted, ref} from 'vue';
import Search from './Search.vue';
import BackButton from "@/components/button/BackButton.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const currentDate = new Date().getFullYear();
const piniaEnumsState = piniaEnumsStore();
const {groupCoachingOperationTypes} = computed(() => piniaEnumsState.enums).value;

const unemployedStatusFiltered = computed(() => {
  return piniaEnumsState.enums.unemployedStatus.filter(status =>
      status.value === 'active' || status.value === 'finished' || status.value === 'new',
  );
});

const filters = ref({
  'officeIds[]': [],
  unemployedId: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
});

const coachingList = ref([]);
const officeDropdown = ref([]);
const unemployedDropdown = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

async function getCoachingList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachingList.value?.pagination?.items_per_page;

  try {
    const {data} = await analyticsUnemployedOverviewService.getGroupCoachingList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachingList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getExcelExport() {
  await downloadFile('/analytics/unemployed-overview/group-coaching-excel-export',
      undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getAllUnemployedDropdown(unemployedDropdown),
    getCoachingList(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>